import { useState, useEffect } from "react";
import SEO from "../components/SEO";
import { fetchCmsCollection, getOptimizedImageUrl } from "../lib/cms";
import LightboxModal from "../components/LightboxModal";
import QuickContactBanner from "../components/QuickContactBanner";

export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [galleryItems, setGalleryItems] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  const categories = [
    { key: "all", label: "Alle projekter", icon: "fa-solid fa-border-all" },
    { key: "tag", label: "Tagarbejde", icon: "fa-solid fa-house-chimney" },
    { key: "vinduer-doere", label: "Vinduer & døre", icon: "fa-solid fa-door-open" },
    { key: "tilbygning-vinterhave", label: "Tilbygninger", icon: "fa-solid fa-tree-city" },
    { key: "snedkerarbejde", label: "Snedker & køkken", icon: "fa-solid fa-ruler-combined" },
    { key: "renovering", label: "Renovering", icon: "fa-solid fa-hammer" },
    { key: "terrasser", label: "Terrasser & ude", icon: "fa-solid fa-chair" }
  ];

  useEffect(() => {
    async function loadGallery() {
      const params = { sort: "order", order: "asc", limit: 60 };
      if (activeCategory !== "all") {
        params.term = `gallery-category:${activeCategory}`;
      }
      const data = await fetchCmsCollection("gallery", params);

      if (data.items && data.items.length > 0) {
        setGalleryItems(data.items);
      } else {
        // In fallback or if server term filtering returns empty, fetch all and filter in JS
        const all = await fetchCmsCollection("gallery", { sort: "order", order: "asc" });
        if (activeCategory === "all") {
          setGalleryItems(all.items || []);
        } else {
          const filtered = (all.items || []).filter((item) =>
            item.terms?.["gallery-category"]?.includes(activeCategory)
          );
          setGalleryItems(filtered.length > 0 ? filtered : all.items || []);
        }
      }
    }
    loadGallery();
  }, [activeCategory]);

  const openLightbox = (index) => {
    setCurrentImgIndex(index);
    setLightboxOpen(true);
  };

  return (
    <>
      <SEO
        title="Galleri - Tømrermester Jacob"
        description="Se et udvalg af vores udførte tømrer- og snedkerarbejder, nyt tag, vinduer, tilbygninger og renoveringer i Ribe og omegn."
        ogTitle="Galleri - Tømrermester Jacob"
        ogDescription="Se et udvalg af vores udførte tømrer- og snedkerarbejder."
      />

      {/* Hero */}
      <section className="bg-stone-100/60 border-b border-stone-200 py-12 lg:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-red-100 text-[#ce0611] text-xs font-semibold uppercase tracking-wider mb-4">
            <i className="fa-solid fa-images text-[#ce0611]" aria-hidden="true"></i>
            <span>Billeder & Referencer</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-stone-900 mb-4">
            Galleri og udførte projekter
          </h1>
          <p className="text-base sm:text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
            Her kan du se eksempler på vores håndværksmæssige løsninger inden for tag, vinduer, tilbygninger, snedkerkøkkener og renovering.
          </p>
        </div>
      </section>

      {/* Category filters & Grid */}
      <section className="py-12 sm:py-16 bg-white min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter pills */}
          <div className="flex items-center justify-center flex-wrap gap-2 sm:gap-3 mb-10">
            {categories.map((cat) => {
              const isActive = activeCategory === cat.key;
              return (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => setActiveCategory(cat.key)}
                  className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer ${
                    isActive
                      ? "bg-[#ce0611] text-white shadow-sm font-semibold"
                      : "bg-stone-100 text-stone-700 hover:bg-stone-200/80 hover:text-stone-900 border border-stone-200/60"
                  }`}
                >
                  <i className={`${cat.icon} text-xs`} aria-hidden="true"></i>
                  <span>{cat.label}</span>
                </button>
              );
            })}
          </div>

          {/* Grid */}
          {galleryItems.length === 0 ? (
            <div className="text-center py-16 text-stone-500">
              <i className="fa-solid fa-spinner fa-spin text-2xl text-[#ce0611] mb-3" aria-hidden="true"></i>
              <p>Indlæser billeder...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {galleryItems.map((item, index) => {
                const imgUrl = item.image?.[0] || "";
                return (
                  <div
                    key={item.id || index}
                    onClick={() => openLightbox(index)}
                    className="group relative rounded-xl overflow-hidden bg-stone-100 border border-stone-200/80 aspect-[4/3] cursor-pointer shadow-xs hover:shadow-md transition-all"
                  >
                    <img
                      src={getOptimizedImageUrl(imgUrl, { width: 600 })}
                      alt={item.alt || item.title || "Projekt"}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-4 text-white">
                      <h3 className="font-semibold text-sm line-clamp-1">{item.title}</h3>
                      {item.description && (
                        <p className="text-stone-300 text-xs line-clamp-2 mt-0.5">{item.description}</p>
                      )}
                      <span className="text-[11px] text-red-300 flex items-center mt-2 font-medium">
                        <i className="fa-solid fa-magnifying-glass-plus mr-1.5" aria-hidden="true"></i>
                        Forstør billede
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <QuickContactBanner />

      <LightboxModal
        images={galleryItems}
        currentIndex={currentImgIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNavigate={(idx) => setCurrentImgIndex(idx)}
      />
    </>
  );
}
